import { ComponentType } from 'react';
import * as R from 'remeda';
import { FormattedMessage, useIntl } from 'react-intl';
import PaperSection from '@rossum/components/paper-section/PaperSection';
import { IconMail, IconWebhook } from '@rossum/ui/icons/tabler';
import {
  Divider,
  Link,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { docsLinks } from '../../../../../constants/values';

type Setting = {
  Icon: ComponentType;
  translationId: 'inbox' | 'webhooks';
  link?: string;
  Button?: () => JSX.Element;
};

type Props = { inboxExists: boolean };

const settings = ({ inboxExists }: Props): Array<Setting> =>
  R.pipe(
    [
      !inboxExists && {
        Icon: IconMail,
        translationId: 'inbox' as const,
        link: docsLinks.inbox,
      },
      {
        Icon: IconWebhook,
        translationId: 'webhooks' as const,
        link: docsLinks.webhooks,
      },
    ],
    R.filter(R.isTruthy)
  );

const Advanced = ({ inboxExists }: Props) => {
  const intl = useIntl();

  return (
    <PaperSection
      title={intl.formatMessage({
        id: 'containers.settings.queues.titles.advanced',
      })}
    >
      <Stack spacing={2} divider={<Divider />}>
        {settings({ inboxExists }).map(
          ({ Icon, Button, translationId, link }) => (
            <Stack
              spacing={2}
              direction="row"
              justifyContent="space-around"
              alignItems="center"
              px={2.5}
              sx={{
                width: 1,
                maxWidth: '600px',
              }}
              key={`advanced-section-${translationId}`}
            >
              <Stack
                component={Paper}
                elevation={6}
                justifyContent="center"
                alignItems="center"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: borderRadius('xl'),
                  boxShadow: 'none',
                }}
              >
                <SvgIcon fontSize="large" color="primary">
                  <Icon />
                </SvgIcon>
              </Stack>
              <Stack>
                <Typography variant="body1">
                  <FormattedMessage
                    id={`containers.settings.queues.advanced.${translationId}.title`}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: '370px' }}
                >
                  <FormattedMessage
                    id={`containers.settings.queues.advanced.${translationId}.text`}
                  />
                </Typography>
              </Stack>

              {link && (
                <Link
                  href={link}
                  rel="noopener noreferrer"
                  target="_blank"
                  data-cy="basic-settings-read-more"
                >
                  <FormattedMessage id="containers.settings.queues.advanced.link" />
                </Link>
              )}
              {Button && <Button />}
            </Stack>
          )
        )}
      </Stack>
    </PaperSection>
  );
};

export default Advanced;
